import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";

import "../../../assets/css/AGGrid-custom.css";
import { CustomButton } from "../../../components/common/customButton";
import { CustomMultiSelect } from "../../../components/common/inputFields/customMultiSelect";
import { ErrorDialog } from "../../../components/common/errorDialog";
import { useReferenceLists } from "../../../context/GlobalGetLists";
import { useErrorDialog } from "../../../hooks/useErrorDialog";
import { apiGet } from "../../../services/genesisApi/httpsMethods";
import { exportAdvisorFeesToExcel } from "../../../services/summaryExportService";
import type { Country, DealAdvisorFee, Sector, Status } from "../../../types/deal";
import { numberFormatter } from "../../../utils/numberFormatter";

ModuleRegistry.registerModules([AllCommunityModule]);

function buildAdvisorFeesUrl(params: {
  statusIds: number[];
  countryIds: number[];
  sectorIds: number[];
}): string {
  const parts: string[] = [];
  for (const id of params.statusIds) {
    parts.push(`status_ids=${id}`);
  }
  for (const id of params.countryIds) {
    parts.push(`country_ids=${id}`);
  }
  for (const id of params.sectorIds) {
    parts.push(`sector_ids=${id}`);
  }
  return parts.length > 0 ? `deal-advisor-fees?${parts.join("&")}` : "deal-advisor-fees";
}

function DealNameCell(params: ICellRendererParams<DealAdvisorFee>) {
  const dealId = params.data?.deal?.id ?? params.data?.deal_id;
  const dealName =
    params.data?.deal?.deal_name ??
    (dealId != null ? `Deal ${dealId}` : "");
  if (dealId == null) return <span>{dealName}</span>;
  return (
    <Link
      to={`/deals/${dealId}`}
      style={{ color: "#1B7A63", textDecoration: "underline" }}
    >
      {dealName}
    </Link>
  );
}

export function AdvisorFeesTab() {
  const [fees, setFees] = useState<DealAdvisorFee[]>([]);
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<Status[]>([]);
  const [countryFilter, setCountryFilter] = useState<Country[]>([]);
  const [sectorFilter, setSectorFilter] = useState<Sector[]>([]);

  const { status, countries, sectors } = useReferenceLists();
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  const statusIds = useMemo(
    () => statusFilter.map((s) => s.id),
    [statusFilter],
  );
  const countryIds = useMemo(
    () => countryFilter.map((c) => c.id),
    [countryFilter],
  );
  const sectorIds = useMemo(
    () => sectorFilter.map((s) => s.id),
    [sectorFilter],
  );

  const filterKey = useMemo(
    () =>
      [
        statusIds.slice().sort((a, b) => a - b).join(","),
        countryIds.slice().sort((a, b) => a - b).join(","),
        sectorIds.slice().sort((a, b) => a - b).join(","),
      ].join("|"),
    [statusIds, countryIds, sectorIds],
  );

  useEffect(() => {
    // Refetch when filter selection changes (filterKey). The `cancelled` flag
    // drops responses from superseded requests so a slow earlier filter
    // selection cannot overwrite the results of a newer one.
    let cancelled = false;
    setLoading(true);
    const url = buildAdvisorFeesUrl({ statusIds, countryIds, sectorIds });
    apiGet<DealAdvisorFee[]>(url)
      .then((response) => {
        if (cancelled) return;
        if (response.success && response.data) {
          setFees(response.data);
          return;
        }
        showErrorFromResponse(response);
        setFees([]);
      })
      .catch(() => {
        if (cancelled) return;
        showErrorFromResponse({
          success: false,
          status: 500,
          message: "Failed to fetch advisor fees. Please try again.",
        });
        setFees([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const handleExportToExcel = useCallback(() => {
    setIsExporting(true);
    try {
      exportAdvisorFeesToExcel({
        fees,
        statusFilter,
        countryFilter,
        sectorFilter,
      });
    } catch (error) {
      showErrorFromResponse({
        success: false,
        status: 500,
        message:
          error instanceof Error
            ? error.message
            : "Failed to export advisor fees. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  }, [fees, statusFilter, countryFilter, sectorFilter, showErrorFromResponse]);

  const columnDefs = useMemo((): ColDef<DealAdvisorFee>[] => {
    return [
      {
        headerName: "Deal",
        colId: "deal_name",
        flex: 1,
        minWidth: 180,
        valueGetter: (params: ValueGetterParams<DealAdvisorFee>) =>
          params.data?.deal?.deal_name ??
          (params.data?.deal_id != null ? `Deal ${params.data.deal_id}` : ""),
        cellRenderer: DealNameCell,
      },
      {
        headerName: "Status",
        colId: "status",
        width: 140,
        valueGetter: (params: ValueGetterParams<DealAdvisorFee>) =>
          params.data?.deal?.status?.status ?? "",
      },
      {
        headerName: "Add-On",
        colId: "accordion_name",
        width: 160,
        valueGetter: (params: ValueGetterParams<DealAdvisorFee>) =>
          params.data?.accordion_name ?? "",
      },
      {
        field: "service",
        headerName: "Service",
        width: 160,
      },
      {
        field: "firm",
        headerName: "Firm",
        width: 200,
      },
      {
        field: "task",
        headerName: "Task",
        flex: 1,
        minWidth: 160,
      },
      {
        field: "currency",
        headerName: "Currency",
        width: 110,
      },
      {
        field: "quote_amount",
        headerName: "Quote Amount",
        width: 140,
        valueFormatter: numberFormatter,
        cellStyle: { textAlign: "right" },
      },
      {
        field: "actual_amount",
        headerName: "Actual Amount",
        width: 140,
        valueFormatter: numberFormatter,
        cellStyle: { textAlign: "right" },
      },
      {
        field: "billable_hours_total_amount",
        headerName: "Billable Hours Total",
        width: 170,
        valueFormatter: numberFormatter,
        cellStyle: { textAlign: "right" },
      },
    ];
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0,
        width: "100%",
        minWidth: 0,
        pt: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 1.5,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
          <CustomMultiSelect
            value={statusFilter.map((s) => s.id)}
            onChange={(e) => {
              const selectedIds = e.target.value as (string | number)[];
              setStatusFilter(
                status.filter((opt: Status): opt is Status =>
                  selectedIds.includes(opt.id),
                ),
              );
            }}
            options={status}
            placeholder="Select Status"
            labelKey="status"
            minWidth="160px"
          />
          <CustomMultiSelect
            value={countryFilter.map((c) => c.id)}
            onChange={(e) => {
              const selectedIds = e.target.value as (string | number)[];
              setCountryFilter(
                countries.filter((opt: Country) =>
                  selectedIds.includes(opt.id),
                ),
              );
            }}
            options={countries}
            placeholder="Countries"
            labelKey="nicename"
            minWidth="140px"
          />
          <CustomMultiSelect
            value={sectorFilter.map((s) => s.id)}
            onChange={(e) => {
              const selectedIds = e.target.value as (string | number)[];
              setSectorFilter(
                sectors.filter((opt: Sector) =>
                  selectedIds.includes(opt.id),
                ),
              );
            }}
            options={sectors}
            placeholder="Sectors"
            labelKey="sector"
            minWidth="140px"
          />
        </Box>
        <CustomButton
          handleButtonPress={handleExportToExcel}
          isButtonDisabled={isExporting || loading || fees.length === 0}
          text="Export to Excel"
          isLoading={isExporting}
          isLoadingText="Exporting…"
          solid={false}
          widthOverride="140px"
        />
      </Box>

      <Typography sx={{ fontSize: "13px", color: "#182737", mb: 1 }}>
        {loading ? (
          <>Loading advisor fees…</>
        ) : (
          <>
            Showing <strong>{fees.length}</strong>{" "}
            {fees.length === 1 ? "fee" : "fees"}
          </>
        )}
      </Typography>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ width: "100%", height: 650 }}>
          <div className="ag-theme-alpine" style={{ height: "100%", width: "100%" }}>
            <AgGridReact<DealAdvisorFee>
              rowData={fees}
              columnDefs={columnDefs}
              defaultColDef={{
                flex: 0,
                resizable: true,
                sortable: true,
                filter: true,
              }}
              rowHeight={30}
              headerHeight={30}
              suppressCellFocus
              getRowId={({ data }) => String(data.id)}
              overlayNoRowsTemplate="No advisor fees match the current filters."
            />
          </div>
        </Box>
      )}

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
}
